<template>
    <header v-if="largeHeader" id="header" class="d-flex align-items-center justify-content-between">
        <ChatStats />
        <div class="d-flex header-selection-main" style="align-items: center; margin-right: 30px">
            <div class="d-flex header-selection-main" style="align-items: center">
                <button
                    v-if="newVersionAvailable"
                    class="custom-version-btn"
                    @click="reloadPage"
                    v-tippy="{
                    followCursor: true,
                    placement: 'bottom-end',
                    theme: isDarkMode ? 'light-border' : 'google',
                }"
                    :content="$t('message.newVersion')"
                >
                    <NewVersionUpdate />
                    {{ $t("message.newVersionShort") }}
                </button>
            </div>
            <md-button
                v-tippy="{
                    followCursor: true,
                    placement: 'bottom-end',
                    theme: isDarkMode ? 'light-border' : 'google',
                }"
                :content="$t('message.errorReport')"
                class="custom-button"
                @click="makeBugReport"
            >
                <md-icon :style="{ color: darkMode ? 'yellow' : 'black' }">report</md-icon>
            </md-button>

            <HeaderRight />
        </div>

        <a-modal
            id="make-bug-report"
            :footer="null"
            :title="$t('message.sendErrorFeedBack')"
            :visible="errorModalVisible"
            @cancel="errorModalVisible = false"
            @ok="errorModalVisible = false"
        >
            <ErrorReport :file-url="errorFileURl" @changeVisibility="errorModalVisible = false" />
        </a-modal>
    </header>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
    faCheckCircle,
    faClock,
    faMinusCircle,
    faPowerOff,
    faSignOutAlt,
    faUserCog,
} from "@fortawesome/free-solid-svg-icons";
import ChatStats from "./ChatStats";
import Avatar from "vue-avatar";
import KeyboardShortcuts from "./KeyboardShortcuts";
import Version from "../components/svg/Version";
import Keyboard from "../components/svg/Keyboard";
import Online from "../components/svg/Online";
import Away from "../components/svg/Away";
import Busy from "../components/svg/Busy";
import Offline from "../components/svg/Offline";
import SocketConnect from "./svg/SocketConnect";
import { log2 } from "../utils/log2";
import CountryFlag from "vue-country-flag";
import Vue from "vue";
import { sendSocketMessage } from "../utils/socketStream";
import * as _ from "lodash";
import version from "../utils/version";
import { onNewVersionReceived } from "../utils/utils";
import NewVersionUpdate from "./svg/NewVersionUpdate";
import ErrorReport from "./ErrorReport";
import HeaderRight from "./HeaderRight";

library.add(faCheckCircle, faClock, faMinusCircle, faPowerOff, faUserCog, faSignOutAlt);
const log = log2("Header");
Vue.component("country-flag", CountryFlag);

export default {
    name: "Header",
    components: {
        HeaderRight,
        ChatStats,
        Avatar,
        KeyboardShortcuts,
        Version,
        Keyboard,
        Online,
        Away,
        Busy,
        Offline,
        NewVersionUpdate,
        SocketConnect,
        ErrorReport,
    },
    data() {
        return {
            keyboardShortCutsModalVisible: false,
            zoomMaxRangeBound: 125,
            zoomMinRangeBound: 75,
            zoomValue: 100,
            modalVisible: false,
            version,
            newVersionAvailable: false,
            volume: -1,
            isOpenLang: false,
            customerDomainsFetched: false,
            cancels: [],
            newMessage: 0,
            errorModalVisible: false,
            errorFileURl: "",
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            adminUser: (state) => state.auth.adminUser,
            menu: (state) => state.layout.menu,
            site: (state) => state.site.site,
            zoom: (state) => state.layout.zoom,
            settings: (state) => state.setting.settings,
            fakeMode: (state) => state.app.fakeMode,
            socketConnected: (state) => state.socketStatus.socket_connected,
            offlineMode: (state) => state.setting.offlineMode,
            debugMode: (state) => state.app.debugMode,
            socket: (state) => state.socketStatus.current_socket,
            visitorScreenUrl: (state) => {
                const isProduction = process.env.ENVIRONMENT === "production";
                const isStage = process.env.ENVIRONMENT === "stage";
                const siteAlias = (state.site.site.customerClientAddress || "").replace(".visitor.supsis.live", "");
                const protocol = isProduction || isStage ? "https" : "http";
                const hostname = isProduction
                    ? "demo-site.supsis.live?customer=" + siteAlias
                    : isStage
                    ? `demo-site.supsis.live?url=${protocol}://${
                          state.site.site.customerClientAddress || "betavisitor.supsis.live"
                      }/static/js/loader.js`
                    : "localsite.supsis.live?customer=local";
                return `${protocol}://${hostname}`;
            },
            customerDomains: (state) => state.visitor.customerDomains,
            popovers: (state) => state.app.headerPopoverStatus,
            privateChatIsOpen: (state) => state.app.privateChatIsOpen,
            unreadCount: (state) => state.app.unreadPrivateChatCount,
            largeHeader: (state) => state.app.largeHeader,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        darkMode: {
            set(value) {
                this.updateDarkMode(value);
            },
            get() {
                return this.isDarkMode;
            },
        },
    },
    methods: {
        ...mapActions({
            setUserStatus: "auth/setStatus",
            changeZoom: "layout/changeZoom",
            refreshAuthUser: "auth/refreshAuthUser",
            getUsers: "user/getUsers",
            getUser: "user/getUser",
            updateUser: "user/updateUser",
            updateDarkMode: "user/updateDarkMode",
            togglePrivateChatVisibleState: "app/togglePrivateChatVisibleState",
            setChatFakeMode: "app/setChatFakeMode",
            getCustomerDomains: "visitor/getCustomerDomains",
        }),

        makeBugReport() {
            const capture = async () => {
                const canvas = document.createElement("canvas");
                try {
                    const stream = await navigator.mediaDevices.getDisplayMedia({
                        video: { mediaSource: "screen" },
                    });
                    // get correct video track
                    const track = stream.getVideoTracks()[0];
                    const imageCapture = new ImageCapture(track);
                    // take first frame only
                    setTimeout(async () => {
                        const bitmap = await imageCapture.grabFrame();
                        track.stop();
                        canvas.width = bitmap.width;
                        canvas.height = bitmap.height;
                        const context = canvas.getContext("2d");
                        context.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height);
                        this.errorFileURl = canvas.toDataURL();
                        this.errorModalVisible = true;
                    }, 1000);
                } catch (err) {
                    console.error("Error: " + err);
                }
            };
            capture();
        },
        toggleChat() {
            this.togglePrivateChatVisibleState();
        },

        integrationScript() {
            const isProduction = process.env.PRODUCTION === "true";
            const hostname = this.site.customerClientAddress;
            const protocol = isProduction ? "https" : "http";
            const path = isProduction ? "static/js/loader.js" : "loader.js";
            const commandHandlerScript =
                `<script>window.supsis=window.supsis||function(){(supsis.q=supsis.q||[]).push(arguments)};supsis.l=+new Date;` +
                ["<", "/", "script>"].join("").concat("\n");
            return (
                commandHandlerScript +
                `<script src=\"${protocol}://${hostname}/${path}\" type=\"text/javascript\" async defer>` +
                ["<", "/", "script>"].join("")
            );
        },
        integrationDemoScript() {
            return `<html>
<head>
    <meta name="viewport" content="initial-scale=1, maximum-scale=1, user-scalable=no">
    <title>Sitenizin Başlığı</title>
    ${this.integrationScript()}
</head>
<body>
 ......
</body>
</html>
`;
        },
        isSiteIntegrated() {
            return this.customerDomainsFetched && this.customerDomains.length;
        },
        reloadPage() {
            window.location.reload();
        },
        updateVolume: _.debounce(async function (volume, showNotification = true) {
            log.debug("update volume called", this);
            this.user.audioAlertVolume = volume / 100;
            await this.updateUser({ ...this.user });
            await this.refreshAuthUser();
            if (showNotification)
                this.$toasted.show(this.$t("message.userVolumeUpdated"), {
                    duration: 3000,
                    position: "bottom-center",
                });
        }, 1000),
        updateZoom: _.debounce(function (value) {
            this.changeZoom(value);
        }, 1000),
        disconnectSocket() {
            if (this.debugMode && this.socketConnected) {
                this.socket.disconnect();
            }
        },
        toggleKeyboardShotcutModal(visible) {
            log.debug("toggleKeyboardShotcutModal called", visible);
            this.keyboardShortCutsModalVisible = visible;
        },
        setStatus(status) {
            sendSocketMessage("user status-change", {
                userId: this.user._id,
                status: status,
                siteId: this.site._id,
            });

            this.setUserStatus(status);
        },
    },
    mounted() {
        log("mounted zoom", this.zoom);
        this.zoomValue = this.zoom;
        /* this.darkMode.isActive = this.isDarkMode*/
        document.body.style.zoom = this.zoom + "%";
        this.$forceUpdate();
        //Get new version information If ther is new version ask to reload with button
        onNewVersionReceived.then((x) => {
            this.newVersionAvailable = true;
        });
        this.getCustomerDomains().then((x) => {
            this.customerDomainsFetched = true;
        });
    },
    watch: {
        zoom(newValue) {
            log("zoom value changed", newValue);
            document.body.style.zoom = newValue + "%";
            this.$forceUpdate();
        },
        zoomValue(newValue, oldValue) {
            this.updateZoom(newValue);
        },
        volume(newValue, oldValue) {
            log.debug("volume change", { newValue, oldValue });
            if (newValue !== oldValue && oldValue !== -1) this.updateVolume(newValue, oldValue >= 0);
        },
        user(newUser) {
            log.debug("newUser", newUser);
            this.volume = newUser.audioAlertVolume ? newUser.audioAlertVolume * 100 : 0;
        },
        "popovers.isSettingPopoverOpen": function (newValue) {
            if (!newValue) {
                this.isOpenLang = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.new-theme {
    #header {
        padding-left: 0px;
        border-bottom: 1px solid #ebedf3;
        height: 45px;
    }

    .custom-button {
        width: 35px;
        min-width: 35px;
        height: 35px;
        background-color: #f3f6f9;
        border-radius: 8px;
        margin: 8px;
    }

    .custom-version-btn {
        height: 35px;
        background-color: #f3f6f9;
        border-radius: 8px;
        margin: 8px;
        text-transform: capitalize !important;
        display: flex;
        align-items: center;
        flex-direction: row;
        color: #448aff;
        border: none;

        svg {
            display: flex;
            align-items: center;
            fill: #448aff;
            margin-right: 4px;
        }
    }

    .avatarSection {
        display: flex;
        align-items: center;

        span {
            font-family: Poppins;
            font-size: 13px;
            color: #b5b5c3;

            strong {
                font-family: Poppins;
                font-size: 13px;
                color: #7e8299;
            }
        }

        .avatar {
            border-radius: 8px;
            width: 35px;
            height: 35px;
            display: flex;
            justify-content: center;
            align-items: center;
            font-family: Poppins;
            font-size: 16px;

            &.online {
                color: #1bc5bd;
                background-color: #c9f7f5;
            }

            &.offline {
                color: #f64e60;
                background-color: #fdbec5;
            }

            &.away {
                color: #3699ff;
                background-color: #acd5ff;
            }

            &.busy {
                color: #ffa829;
                background-color: #ffe6c1;
            }
        }
    }
}

.dark-mode {
    .custom-button {
        background-color: #343441;
    }
}
</style>
<style>
.ant-popover-inner-content {
    box-shadow: 0 3px 6px #00000080;
}

.cursorPointer {
    cursor: pointer;
}

.cursorPointer:hover {
    font-weight: 600;
}

.popoverFont {
    font-family: Poppins;
    font-size: 14px;
    color: #181c32;
    font-weight: 300;
}
</style>
